//////////////////
// IMPORTS
//////////////////
import * as ISchema from "@schema";
import { ZonedMoment } from "@util/time-zone.util";
import { connect } from "react-redux";
// libraries
import * as React from "react";
import { createRefetchContainer } from "react-relay";
import pluralize from "pluralize";
// suggestion-body imports
import { IProps, IContainer, IState } from "./SuggestionBodyWeeklyAutopilotSummary.d";
import {
  suggestionBodyWeeklyAutopilotSummaryFragments,
  refetchQuery,
} from "./SuggestionBodyWeeklyAutopilotSummary.gql";
import { styles, activity } from "./SuggestionBodyWeeklyAutopilotSummary.styles";
import { autopilotWeeklySummarySurveyResponseDate } from "@state/local-storage";
import { weeklySummaryResponse } from "@mutations";
// other components
import { AvatarRow } from "@components/avatar";
import { H3 } from "@ui";
// util
import { snackBarOpen } from "@actions/snack-bar.actions";
import { IReduxState } from "@state/reducers/root.reducer";
// material-ui
import { Button } from "@material-ui/core";
import withStyles from "@material-ui/core/styles/withStyles";

//////////////////
// COMPONENT
//////////////////
class SuggestionBodyWeeklyAutopilotSummaryCmpt extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);
    this.state = {
      personList: null,
      showWeeklySummaryResponseSurvey: !autopilotWeeklySummarySurveyResponseDate.get(),
      selectedFeedback: null,
    };
  }

  private onFeedbackSelect = (feedback: ISchema.WeeklySummaryFeedback) => {
    const { relay, org, suggestionPayload } = this.props;
    this.setState({ selectedFeedback: feedback });

    // send the feedback
    const input = {
      feedback,
      orgRelayId: org.id,
      minDate: suggestionPayload.dateRangeMin,
      maxDate: suggestionPayload.dateRangeMax,
    };
    weeklySummaryResponse(relay.environment, input, this.onFeedBackSuccess, () =>
      this.props.snackBarOpen("Sorry, an error occurred recording your response."),
    );
  };

  private onFeedBackSuccess = () => {
    this.props.snackBarOpen("Thanks for your feedback");

    // remember for the future
    autopilotWeeklySummarySurveyResponseDate.set(new ZonedMoment());
  };

  public componentDidMount() {
    // load persons for avatars
    this.props.relay.refetch(
      {
        calendarIds: this.props.suggestionPayload.historicalDefragBenefitScore.affectedCalendars,
        orgRelayId: this.props.org.id,
      },
      undefined,
      () => {
        if (this.props.org.personsByCalendarIdsErrorable.__typename === "PersonList") {
          const personList = this.props.org.personsByCalendarIdsErrorable;
          this.setState({ personList });
        }
      },
    );
  }

  private renderFooter = () => {
    const { selectedFeedback, showWeeklySummaryResponseSurvey } = this.state;

    if (!showWeeklySummaryResponseSurvey) {
      return null;
    }

    return (
      <div className={this.props.classes.footer}>
        <span className={this.props.classes.feedback}>Is this summary useful for you?</span>
        <div className={this.props.classes.actions}>
          <Button
            classes={{
              root:
                selectedFeedback === "Yes"
                  ? this.props.classes.selectedActionButton
                  : this.props.classes.unselectedActionButton,
              label: this.props.classes.actionLabel,
            }}
            onClick={() => this.onFeedbackSelect("Yes" as ISchema.WeeklySummaryFeedback)}
          >
            Yes
          </Button>
          <Button
            classes={{
              root:
                selectedFeedback === "No"
                  ? this.props.classes.selectedActionButton
                  : this.props.classes.unselectedActionButton,
              label: this.props.classes.actionLabel,
            }}
            onClick={() => this.onFeedbackSelect("No" as ISchema.WeeklySummaryFeedback)}
          >
            No
          </Button>
          <Button
            classes={{
              root:
                selectedFeedback === "NoOpinion"
                  ? this.props.classes.selectedActionButton
                  : this.props.classes.unselectedActionButton,
              label: this.props.classes.actionLabel,
            }}
            onClick={() => this.onFeedbackSelect("NoOpinion" as ISchema.WeeklySummaryFeedback)}
          >
            No Opinion
          </Button>
        </div>
      </div>
    );
  };

  public render() {
    const { suggestionPayload, org, classes } = this.props;
    const { historicalDefragBenefitScore } = suggestionPayload;
    const {
      totalFocusTimeSavings,
      conflictsResolved,
      affectedCalendars,
      meetingsMoved,
    } = historicalDefragBenefitScore;
    const { personList } = this.state;

    return (
      <div>
        <div className={classes.body}>
          <H3 className={classes.heading}>COMPANY-WIDE RESULTS</H3>
          <div style={activity.body}>
            {meetingsMoved && meetingsMoved > 0 ? (
              <div style={activity.row}>
                <span style={activity.stat}>{meetingsMoved}</span>{" "}
                {pluralize("meeting", meetingsMoved)} moved
              </div>
            ) : null}
            <div style={activity.row}>
              <span style={activity.stat}>{affectedCalendars.length}</span>{" "}
              {pluralize("coworker", affectedCalendars.length)} benefited
            </div>
            {totalFocusTimeSavings && totalFocusTimeSavings > 0 ? (
              <div style={activity.row}>
                <span style={activity.stat}>+{totalFocusTimeSavings}</span> hr Focus Time created
              </div>
            ) : null}
            {conflictsResolved && conflictsResolved > 0 ? (
              <div style={activity.row}>
                <span style={activity.stat}>{conflictsResolved}</span> conflicts resolved
              </div>
            ) : null}
          </div>
          <AvatarRow
            org={org}
            personList={personList}
            size={35}
            parentName={"SuggestionBodyWeeklyAutopilotSummary"}
            filterRooms={true}
          />
        </div>
        {this.renderFooter()}
      </div>
    );
  }
}

//////////////////
// STYLES
//////////////////
export const SuggestionBodyWeeklyAutopilotSummaryStyled = withStyles(styles)(
  SuggestionBodyWeeklyAutopilotSummaryCmpt,
);

//////////////////
// REDUX
//////////////////
function mapStateToProps(_state: IReduxState, _ownProps: IContainer) {
  return {};
}

const reduxContainer = connect(mapStateToProps, {
  snackBarOpen,
})(SuggestionBodyWeeklyAutopilotSummaryStyled);

//////////////////
// RELAY
//////////////////
export const SuggestionBodyWeeklyAutopilotSummary = createRefetchContainer<IContainer>(
  reduxContainer,
  suggestionBodyWeeklyAutopilotSummaryFragments,
  refetchQuery,
);
